<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Image from '@uiKit/elements/Image.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'

/** A regulator logo drawn in place of the chip's icon, at its board size. */
export interface TrustChipImage {
  src: string
  width: number
  height: number
  /**
   * Extra zoom inside the box for artwork that carries a wide transparent
   * margin — the board crops the VARA file ~1.22× tighter than `cover` does.
   */
  zoom?: number
}

export interface TrustChipItem {
  label: string
  icon: 'lock' | 'shield' | 'verified'
  /**
   * Optional logo that replaces the icon (the sign-up entry card shows the DLD
   * and VARA marks; login keeps the icons). `icon` stays required so the chip
   * always has a fallback mark.
   */
  image?: TrustChipImage
}

// Regulator / custody cues that sit at the foot of every sign-up card. The
// landing card swaps the third chip to "Client Protected"; everything else
// uses the escrow wording, so the list is overridable but defaulted.
//
// The default list is inlined in the factory rather than hoisted to a const:
// `defineProps` is lifted out of setup(), so it cannot close over a local.
withDefaults(defineProps<{ items?: TrustChipItem[] }>(), {
  items: () => [
    { label: 'DLD Verified', icon: 'verified' },
    { label: 'VARA Regulated', icon: 'shield' },
    { label: 'Escrow Protected', icon: 'lock' },
  ],
})
</script>

<template>
  <Box class="trust-chips" justify="center" align="center">
    <Box v-for="item in items" :key="item.label" class="trust-chips__item" align="center" gap="1">
      <Box
        v-if="item.image"
        class="trust-chips__logo-frame"
        :style="{ width: `${item.image.width}px`, height: `${item.image.height}px` }"
      >
        <Image
          :src="item.image.src"
          :width="item.image.width"
          :height="item.image.height"
          alt=""
          class="trust-chips__logo"
          :style="item.image.zoom ? { transform: `scale(${item.image.zoom})` } : undefined"
        />
      </Box>
      <PiIcon v-else :name="item.icon" class="trust-chips__icon" />
      <Text is="span" type="caption" color="default">{{ item.label }}</Text>
    </Box>
  </Box>
</template>

<style scoped>
.trust-chips {
  flex-wrap: wrap;
  /* Owned here rather than via Box's `gap` prop: the prop renders a
     `.box--gap-*` class, which ties on specificity with the breakpoint below
     and is resolved only by stylesheet order. */
  gap: 0.5rem;
}

.trust-chips__item {
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--p-surface-100);
  border-radius: var(--p-border-radius-md);
  background: var(--p-surface-0);
}

.trust-chips__icon {
  font-size: 12px;
  color: var(--p-text-muted-color);
  opacity: 0.75;
}

/* The logo files carry their own transparent margin; the board crops them
   into a short strip, which `cover` reproduces at the given box size. */
.trust-chips__logo-frame {
  flex-shrink: 0;
  overflow: hidden;
}

.trust-chips__logo {
  display: block;
  object-fit: cover;
}

/* Three chips at the desktop padding start wrapping to a second row somewhere
   under ~500px, which turns a 24px strip into a 52px one at exactly the width
   where the card can least afford it. Tightening the horizontal padding (never
   the height — the chips are not tap targets) buys roughly another 60px of
   single-row width. With the longest label set ("Client Money Protected") the
   strip still wraps below ~420px; `flex-wrap` keeps that tidy. */
@media (max-width: 480px) {
  .trust-chips {
    gap: 0.25rem;
  }

  .trust-chips__item {
    padding: 0.25rem 0.375rem;
  }
}
</style>
